import baseApi from "../../../common/api/baseApi";
import {
  YES24_PRODUCT_DETAIL_URL,
  RIDI_PRODUCT_DETAIL_URL
} from "../../../common/constants/commonConstant";
import { decodeEntities } from "../../../common/utils/format";

const authHeaders = () => ({
  headers: { token: localStorage.getItem("token") }
});

/**
 * 검색 결과 정규화 형태:
 * {
 *   items: Array<{ id, bookName, price, author?, publisher?, imageUrl?, reqUrl }>,
 *   totalCount: number
 * }
 *
 * 각 source 별로 응답 구조가 다르기 때문에 여기서 통일해서 반환한다.
 * UI(BookSearchModal)는 정규화된 items 만 다룬다.
 */

// ─── YES24 (일반도서) ───────────────────────────────────────
// 백엔드 POST /book/getYes24BookInfo 가 yes24 sniper search 응답 JSON 을 문자열로 wrapping 해서 줌.
// page 는 0-based, pageSize 는 한 페이지 결과 수.
export const searchYes24 = ({ bookName, page = 0, pageSize = 10 }) => {
  return baseApi
    .post(
      "/book/getYes24BookInfo",
      // yes24 SniperSearch 는 JS escape() 의 %uXXXX 형식 query 를 받음 (단권 흐름과 동일).
      // eslint-disable-next-line no-restricted-globals
      { bookName: escape(bookName), page, pageSize },
      authHeaders()
    )
    .then(response => {
      // 백엔드 응답 구조: { result: { retCode, retMsg, data: "<yes24 JSON 문자열>" } }
      const data =
        response.data && response.data.result && response.data.result.data;
      if (!data) return { items: [], totalCount: 0 };
      let parsed;
      try {
        parsed = typeof data === "string" ? JSON.parse(data) : data;
      } catch (e) {
        return { items: [], totalCount: 0 };
      }
      const list = (parsed && parsed.List) || [];
      const items = list.map(b => ({
        id: b.GOODS_NO,
        bookName: decodeEntities(stripTags(b.GOODS_NM || "")),
        price: Number(b.SALE_PR) || 0,
        author: decodeEntities(stripBackticks(b.AUTH_INFO || "")),
        publisher: stripSpecial(b.COMPANY2 || ""),
        publishDate: b.ISS_DM || "", // 예: 20251231
        imageUrl: `https://image.yes24.com/Goods/${b.GOODS_NO}`, // 단권과 동일 (M suffix 빼서 큰 이미지)
        reqUrl: `${YES24_PRODUCT_DETAIL_URL}${b.GOODS_NO}`
      }));
      return { items, totalCount: Number(parsed.TotalCount) || items.length };
    });
};

// ─── 리디북스 (전자책) ──────────────────────────────────────
// 백엔드 POST /book/getRidiBookInfo 가 ridibooks.com 의 Next.js __NEXT_DATA__ JSON 추출 (SPA).
// 응답 wrapping: { result: { retCode, retMsg, data: { totalCount, books: [...] } } }
export const searchRidi = ({ bookName, page = 1 }) => {
  return baseApi
    .post(
      "/book/getRidiBookInfo",
      { bookName, page },
      authHeaders()
    )
    .then(response => {
      const data =
        response.data && response.data.result && response.data.result.data;
      if (!data || typeof data === "string") return { items: [], totalCount: 0 };
      const books = data.books || [];
      const items = books.map((b, idx) => ({
        id: `${b.reqUrl || idx}`,
        bookName: decodeEntities(b.bookName),
        price: Number(String(b.ridiPrice || "0").replace(/[^0-9]/g, "")) || 0,
        author: decodeEntities(b.author),
        publisher: b.publisher,
        imageUrl: b.imageUrl,
        reqUrl: b.reqUrl ? `${RIDI_PRODUCT_DETAIL_URL}${b.reqUrl}` : ""
      }));
      return { items, totalCount: Number(data.totalCount) || items.length };
    });
};

// ─── 교보문고 (전자책) ──────────────────────────────────────
// 백엔드 POST /book/getKyoboBookInfo 가 search.kyobobook.co.kr 의 HTML 파싱 (data-kbbfn-type=EBK 만).
// 응답 wrapping: { result: { retCode, retMsg, data: { totalCount, books: [{bookName, price, author, publisher, imageUrl, reqUrl}, ...] } } }
export const searchKyobo = ({ bookName, page = 1 }) => {
  return baseApi
    .post(
      "/book/getKyoboBookInfo",
      { bookName, page },
      authHeaders()
    )
    .then(response => {
      const data =
        response.data && response.data.result && response.data.result.data;
      if (!data || typeof data === "string") return { items: [], totalCount: 0 };
      const books = data.books || [];
      const items = books.map((b, idx) => ({
        id: `${b.reqUrl || idx}`,
        bookName: decodeEntities(b.bookName),
        price: Number(String(b.price || "0").replace(/[^0-9]/g, "")) || 0,
        author: decodeEntities(b.author),
        publisher: b.publisher,
        imageUrl: b.imageUrl,
        reqUrl: b.reqUrl || ""
      }));
      return { items, totalCount: Number(data.totalCount) || items.length };
    });
};

// ─── helpers ─────────────────────────────────────────────────
function stripTags(s) {
  return (s || "").replace(/<(\/)?([a-zA-Z]+)([^>]*)?>/g, "");
}
function stripBackticks(s) {
  return (s || "").replace(/`/g, "");
}
function stripSpecial(s) {
  return (s || "").replace(
    /[{}[\]/?.,;:|*~`!^\-+<>@#$%&\\=()'"]/g,
    ""
  );
}
